// The order a list is shown in while "Order by category" is on (g81): by
// category first, and inside a category by the list's normal order. It is a
// view. Nothing here reads a clock or a locale or makes an order key, and
// switching the setting on or off rewrites no key, so both builds, and every
// device, draw the same order from the same rows.
//
// The category alone places an item; the grocery word list only fills a blank
// one (grocery/fill.ts) and never decides a position.

import { CATEGORIES } from "./grocery/categories.js";
import { compareOrder, type Ordered } from "./ordering.js";

/** How a list is ordered when it is not in its normal order. A second ordering is another value here. */
export type ListOrder = "category";

export interface Orderable extends Ordered {
  /** Absent on a row from before g79, which reads as none. */
  readonly category?: string | null;
}

/**
 * A category as it is compared: without surrounding space and in lower case.
 * `toLowerCase` takes no locale (`toLocaleLowerCase` is the one that does), so
 * every device folds a category alike. No category folds to "".
 */
export function foldCategory(value: string | null | undefined): string {
  return value ? value.trim().toLowerCase() : "";
}

const AISLES = new Map<string, number>(CATEGORIES.map((category) => [foldCategory(category.label), category.rank]));

// Shopping's aisles, then a list's own categories, then items with none.
const AISLE = 0;
const OWN = 1;
const NONE = 2;

interface Place {
  readonly tier: number;
  /** The aisle's rank in tier 0, and zero elsewhere. */
  readonly rank: number;
  readonly name: string;
}

function placeOf(kind: string, category: string | null | undefined): Place {
  const name = foldCategory(category);
  if (name === "") return { tier: NONE, rank: 0, name };
  // Only a Shopping list has aisles. Elsewhere "Bakery" is a category like any other.
  const rank = kind === "shopping" ? AISLES.get(name) : undefined;
  return rank === undefined ? { tier: OWN, rank: 0, name } : { tier: AISLE, rank, name };
}

function comparePlace(a: Place, b: Place): number {
  if (a.tier !== b.tier) return a.tier - b.tier;
  if (a.rank !== b.rank) return a.rank - b.rank;
  // By UTF-16 code unit, as `compareOrder` compares keys, and never
  // `localeCompare`, whose answer depends on the device's language.
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  return 0;
}

/**
 * Names the group an item is shown in. Two items are in one category exactly
 * when this is equal for both, which is what bounds a move (g81).
 */
export function categoryGroup(kind: string, category: string | null | undefined): string {
  const place = placeOf(kind, category);
  return place.tier === NONE ? "" : `${place.tier}:${place.name}`;
}

/** The comparison a list is drawn by. With no ordering it is the normal order, so a caller has one path. */
export function compareForDisplay(kind: string, order: ListOrder | null | undefined): (a: Orderable, b: Orderable) => number {
  if (order !== "category") return compareOrder;
  return (a, b) => comparePlace(placeOf(kind, a.category), placeOf(kind, b.category)) || compareOrder(a, b);
}

/** `items` in the order the list is shown in. The input is left as it was. */
export function displayOrder<T extends Orderable>(kind: string, order: ListOrder | null | undefined, items: readonly T[]): T[] {
  if (order !== "category") return [...items].sort(compareOrder);
  // Each place is worked out once, not once a comparison.
  return items
    .map((item) => ({ item, place: placeOf(kind, item.category) }))
    .sort((a, b) => comparePlace(a.place, b.place) || compareOrder(a.item, b.item))
    .map((entry) => entry.item);
}
